import React, { PureComponent } from 'react';
import { Map } from 'immutable';
import PropTypes from 'prop-types';
import Translator from 'translator';
import memoizeOne from 'memoize-one';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { withRouter } from 'react-router-dom';
import { withStyles } from '@material-ui/core';
import { FHETable } from 'data/components';
import { Toastr, tools } from 'data/common';
import renderEnvComponent from 'data/common/renderEnvComponent';

import getSelectors from '../selectors';
import { addHomeGenerator, updateHomeGenerator, deleteHomeGenerator } from '../../../data/Generators/actions';

class TableHomeGenerators extends PureComponent {
    static propTypes = {
        addHomeGenerator: PropTypes.func.isRequired,
        updateHomeGenerator: PropTypes.func.isRequired,
        deleteHomeGenerator: PropTypes.func.isRequired,
        homeId: PropTypes.string.isRequired,
        profile: PropTypes.object.isRequired,
        history: PropTypes.object.isRequired,
        homeGenerators: PropTypes.object.isRequired
    };

    constructor(props) {
        super(props);
        this.state = {
            loading: false
        };
    }

    addGenerator = newData => {
        const { addHomeGenerator, homeId, history } = this.props;
        this.setState({ loading: true });
        newData.power = parseInt(newData.power) * 1000;

        addHomeGenerator(homeId, newData)
            .then(() => {
                Toastr.success(Translator.translate(`generic.home.generatorTable.actions.add.success`));
            })
            .catch(err => {
                const message = Translator.translateWithFallback(`api.message.${err.message ? err.message : err}`, 'api.message.default');
                Toastr.error(Translator.translate(`generic.home.generatorTable.actions.add.error`, message));
            })
            .finally(() => {
                this.setState({ loading: false });
            });
    };
    editGenerator = (newData, rowData) => {
        const { updateHomeGenerator, homeId, history } = this.props;
        this.setState({ loading: true });
        const generator = { ...rowData, ...newData };
        delete generator.id;
        generator.power = parseInt(generator.power) * 1000;
        updateHomeGenerator(homeId, rowData.id, generator)
            .then(() => {
                Toastr.success(Translator.translate(`generic.home.generatorTable.actions.update.success`));
            })
            .catch(err => {
                const message = Translator.translateWithFallback(`api.message.${err.message ? err.message : err}`, 'api.message.default');
                Toastr.error(Translator.translate(`generic.home.generatorTable.actions.update.error`, message));
            })
            .finally(() => {
                this.setState({ loading: false });
            });
    };
    deleteGenerator = rowData => {
        const { deleteHomeGenerator, homeId, history } = this.props;
        Toastr.confirm(
            Translator.translate(`generic.home.generatorTable.actions.delete.confirmDelete`, rowData.id),
            Translator.translate(`generic.home.generatorTable.actions.delete.confirmDeleteTitle`),
            {
                onYes: () => {
                    this.setState({ loading: true });
                    deleteHomeGenerator(homeId, rowData.id)
                        .then(() => {
                            Toastr.success(Translator.translate(`generic.home.generatorTable.actions.delete.success`));
                        })
                        .catch(err => {
                            const message = Translator.translateWithFallback(`api.message.${err.message ? err.message : err}`, 'api.message.default');
                            Toastr.error(Translator.translate(`generic.home.generatorTable.actions.delete.error`, message));
                        })
                        .finally(() => {
                            this.setState({ loading: false });
                        });
                }
            }
        );
    };

    renderActions = memoizeOne(() => {
        let actions = {
            row: [],
            toolbar: []
        };
        return actions;
    });

    renderData = memoizeOne(homeGenerators => {
        let result = [];

        homeGenerators.map((generator, id) => {
            result.push({
                id: id,
                orientation: generator.get('orientation'),
                generatorType: generator.get('generatorType'),
                power: parseInt(generator.get('power')),
                roofPitch: parseInt(generator.get('roofPitch')),
                moduleType: generator.get('moduleType')
            });
        });

        return result;
    });

    renderColumns = memoizeOne(() => {
        return [
            {
                title: Translator.translate('general.table.column.id'),
                name: 'id',
                unEditable: true
            },
            {
                title: Translator.translate('general.table.column.orientation'),
                name: 'orientation',
                render: (row, data, value) => {
                    if (!value) return null;
                    return Translator.translateWithFallback(`general.orientation.${value}`, '');
                },
                selectRender: {
                    SE: Translator.translate('general.orientation.SE'),
                    SSE: Translator.translate('general.orientation.SSE'),
                    S: Translator.translate('general.orientation.S'),
                    SSO: Translator.translate('general.orientation.SSO'),
                    SO: Translator.translate('general.orientation.SO'),
                    OSO: Translator.translate('general.orientation.OSO'),
                    O: Translator.translate('general.orientation.O'),
                    ONO: Translator.translate('general.orientation.ONO'),
                    NO: Translator.translate('general.orientation.NO'),
                    NNO: Translator.translate('general.orientation.NNO'),
                    N: Translator.translate('general.orientation.N'),
                    NNE: Translator.translate('general.orientation.NNE'),
                    NE: Translator.translate('general.orientation.NE'),
                    ENE: Translator.translate('general.orientation.ENE'),
                    E: Translator.translate('general.orientation.E'),
                    ESE: Translator.translate('general.orientation.ESE')
                }
            },
            {
                title: Translator.translate('general.table.column.generator'),
                name: 'power',
                type: 'integer',
                render: (row, data, value) => {
                    if (!value) return null;
                    return `${value} WC`;
                }
            },
            {
                title: Translator.translate('general.table.column.roofPitch'),
                name: 'roofPitch',
                render: (row, data, value) => {
                    if (!value) return null;
                    return `${value}°`;
                },
                selectRender: {
                    0: '0',
                    5: '5',
                    10: '10',
                    15: '15',
                    20: '20',
                    25: '25',
                    30: '30',
                    35: '35',
                    40: '40',
                    45: '45',
                    50: '50',
                    55: '55',
                    60: '60',
                    65: '65',
                    70: '70',
                    75: '75',
                    80: '80',
                    85: '85',
                    90: '90'
                }
            },
            {
                title: Translator.translate('general.table.column.moduleType'),
                name: 'moduleType',
                render: (row, data, value) => {
                    if (!value) return null;
                    return Translator.translateWithFallback(`general.moduleType.${value}`, '');
                },
                selectRender: {
                    'low-ventilated': Translator.translate('general.moduleType.low-ventilated'),
                    ventilated: Translator.translate('general.moduleType.ventilated'),
                    'well-ventilated': Translator.translate('general.moduleType.well-ventilated')
                }
            }
        ];
    });

    render() {
        const { loading } = this.state;
        const { homeGenerators, profile, history } = this.props;

        const userRights = profile.getIn(['user_rights', history.environment], []);

        return (
            <FHETable
                idTable="homeDetailGenerator"
                columns={this.renderColumns()}
                data={this.renderData(homeGenerators)}
                actions={this.renderActions()}
                isLoading={loading}
                minified
                onAddRow={profile.get('admin') || userRights.includes('homeAddGenerator') ? this.addGenerator : null}
                onEditRow={profile.get('admin') || userRights.includes('homeUpdateGenerator') ? this.editGenerator : null}
                onDeleteRow={profile.get('admin') || userRights.includes('homeDeleteGenerator') ? this.deleteGenerator : null}
                emptyDataSourceMessage={Translator.translate(`generic.home.generatorTable.body.emptyDataSourceMessage`)}
            />
        );
    }
}
function mapStateToProps(state, props) {
    const { history, match } = props;
    const homeId = match.params.homeId;
    const selectors = getSelectors(history.environment);
    return {
        profile: selectors.getProfile(state),
        homeId,
        homeGenerators: selectors.getHomeByHomeId(state, homeId).getIn(['generators'], new Map())
    };
}

const styles = theme => ({
    associated: {
        color: theme.palette.success.main
    },
    dissociated: {
        color: theme.palette.error.main
    },
    connected: {
        color: theme.palette.success.main
    },
    disconnected: {
        color: theme.palette.error.main
    }
});

export default withRouter(
    renderEnvComponent(
        'scenes/Home/tables/TableHomeGenerators',
        connect(
            mapStateToProps,
            (dispatch, ownProps) =>
                bindActionCreators(
                    tools.mergePropsToAction(ownProps.history.environment, { addHomeGenerator, updateHomeGenerator, deleteHomeGenerator }),
                    dispatch
                )
        )(withStyles(styles, { withTheme: true })(TableHomeGenerators))
    )
);



// WEBPACK FOOTER //
// ./environments/genericEnv/scenes/Home/tables/TableHomeGenerators.js